import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { comics, featuredComicId } from "@/mocks/comics";
import type { Comic } from "@/mocks/comics";
import ComicCard from "@/components/base/ComicCard";

const faqs = [
  {
    q: "《再来一次好吗》漫画在哪里可以免费阅读？",
    a: "在36漫画网首页或分类页找到《再来一次好吗》，点击进入漫画详情页即可选择章节进行下拉式免费在线阅读，无需付费、无需注册，随时随地畅享全部中文内容。",
  },
  {
    q: "36漫画网上的漫画都是免费的吗？",
    a: "是的，36漫画网提供海量漫画全集下拉式免费在线阅读服务，所有漫画内容均可免费观看，并支持在手机与电脑上流畅阅读。",
  },
  {
    q: "如何切换漫画的分类？",
    a: "你可以点击顶部的「漫画分类」菜单，或进入「全部漫画」页面，在分类栏中切换恋爱、校园、奇幻、都市、热血、悬疑等不同类别，快速找到心仪的漫画。",
  },
  {
    q: "漫画多久更新一次？",
    a: "人气连载漫画通常每周更新，热门作品甚至每日更新。更新后会第一时间出现在首页的「最新更新」板块，方便你追更。",
  },
];

function Hero() {
  const featured = comics.find((c) => c.id === featuredComicId)!;
  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden flex items-center justify-center" aria-label="精选漫画推荐">
      <img
        src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20anime%20illustration%20of%20a%20young%20couple%20reaching%20toward%20each%20other%20under%20a%20huge%20cherry%20blossom%20tree%20at%20sunset%2C%20warm%20golden%20and%20pink%20palette%2C%20dreamy%20soft%20light%2C%20romantic%20manga%20style%2C%20clean%20expansive%20sky%2C%20high%20detail&width=1600&height=900&seq=hero-main&orientation=landscape"
        alt="《再来一次好吗》漫画 精美场景"
        title="《再来一次好吗》漫画全集下拉式免费阅读"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/60"></div>
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center flex flex-col items-center gap-5 md:gap-6">
        <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 text-background-50 text-xs md:text-sm font-medium px-3 py-1">
          <i className="ri-fire-fill w-4 h-4 flex items-center justify-center" aria-hidden="true" />
          主编力荐 · 今日更新
        </span>
        <h1 className="font-heading text-3xl md:text-5xl text-background-50 drop-shadow-md">《再来一次好吗》</h1>
        <p className="text-sm md:text-base text-background-100/90 max-w-xl leading-relaxed">
          如果时间可以重来，我会在遇见你的那一刻，鼓起勇气说「再来一次好吗」。苏晚与林知夏跨越青春的遗憾与成长，连载至第186话。
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <Link
            to={`/comic/${featuredComicId}`}
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-medium px-6 py-3 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-book-open-line w-5 h-5 flex items-center justify-center" aria-hidden="true" />
            立即阅读
          </Link>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 rounded-md bg-background-50/90 text-foreground-900 text-sm md:text-base font-medium px-6 py-3 whitespace-nowrap hover:bg-background-50 transition-colors cursor-pointer"
          >
            浏览全部漫画
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategorySection() {
  return (
    <section className="w-full mx-auto px-4 md:px-6 py-10 md:py-14" aria-labelledby="cat-title">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 id="cat-title" className="font-heading text-xl md:text-2xl text-foreground-900 flex items-center gap-2">
            <i className="ri-apps-2-line text-primary-600 w-6 h-6 flex items-center justify-center" aria-hidden="true" />
            热门分类
          </h2>
          <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap">
            查看全部 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group flex flex-col items-center gap-3 rounded-lg bg-background-100 border border-background-200/70 px-4 py-6 text-center hover:bg-secondary-100 hover:border-secondary-300 transition-colors"
            >
              <span className="w-12 h-12 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-2xl group-hover:bg-secondary-500 group-hover:text-background-50 transition-colors">
                <i className={`${c.icon} w-6 h-6 flex items-center justify-center`} aria-hidden="true" />
              </span>
              <span className="font-heading text-base text-foreground-900">{c.name}漫画</span>
              <span className="text-xs text-foreground-500 leading-snug line-clamp-2">{c.description}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featured = comics.filter((c) => c.featured || c.rating >= 9.5).slice(0, 4);
  return (
    <section className="w-full bg-background-100/70 py-10 md:py-14" aria-labelledby="featured-title">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 id="featured-title" className="font-heading text-xl md:text-2xl text-foreground-900 flex items-center gap-2">
            <i className="ri-vip-diamond-fill text-accent-500 w-6 h-6 flex items-center justify-center" aria-hidden="true" />
            主编推荐
          </h2>
          <span className="text-xs text-foreground-500 whitespace-nowrap">编辑精心挑选的高分佳作</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {featured.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const latest = comics.slice(0, 8);
  return (
    <section className="w-full mx-auto px-4 md:px-6 py-10 md:py-14" aria-labelledby="latest-title">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 id="latest-title" className="font-heading text-xl md:text-2xl text-foreground-900 flex items-center gap-2">
            <i className="ri-flashlight-fill text-primary-600 w-6 h-6 flex items-center justify-center" aria-hidden="true" />
            最新更新
          </h2>
          <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap">
            更多更新 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {latest.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function RankSection() {
  const popular = useMemo(
    () =>
      [...comics]
        .sort((a, b) => parseFloat(b.views.replace(/[万亿]/g, "").replace(/\./g, "")) - parseFloat(a.views.replace(/[万亿]/g, "").replace(/\./g, "")))
        .slice(0, 6),
    []
  );
  return (
    <section className="w-full bg-background-100/70 py-10 md:py-14" aria-labelledby="rank-title">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <h2 id="rank-title" className="font-heading text-xl md:text-2xl text-foreground-900 flex items-center gap-2 mb-6">
          <i className="ri-medal-fill text-accent-500 w-6 h-6 flex items-center justify-center" aria-hidden="true" />
          人气排行榜
        </h2>
        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {popular.map((c, i) => (
            <li key={c.id}>
              <Link
                to={`/comic/${c.id}`}
                className="flex items-center gap-4 rounded-lg bg-background-50 border border-background-200/70 p-3 hover:border-accent-300 hover:bg-accent-100/40 transition-colors"
              >
                <span
                  className={`font-heading text-2xl w-10 text-center ${i < 3 ? "text-accent-500" : "text-foreground-400"}`}
                >
                  {i + 1}
                </span>
                <img src={c.cover} alt={`${c.title} 封面`} title={c.title} className="w-14 h-20 rounded-md object-cover object-top" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-base text-foreground-900 truncate">{c.title}</h3>
                  <p className="text-xs text-foreground-500 mt-1 flex items-center gap-2">
                    <span className="flex items-center gap-1"><i className="ri-eye-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />{c.views}</span>
                    <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center" aria-hidden="true" />{c.rating.toFixed(1)}</span>
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="relative w-full py-14 md:py-20 overflow-hidden" aria-label="关于36漫画网">
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20colorful%20artistic%20backdrop%20of%20floating%20comic%20books%20and%20speech%20bubbles%20in%20warm%20orange%20and%20cream%20tones%2C%20soft%20bokeh%2C%20dreamy%20and%20creative%2C%20stylized%20illustration&width=1600&height=700&seq=cta-bg&orientation=landscape"
        alt="36漫画网 海量漫画在线阅读"
        title="36漫画网 海量漫画免费阅读"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-700/85 via-primary-800/75 to-primary-900/85"></div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center flex flex-col items-center gap-5">
        <h2 className="font-heading text-2xl md:text-4xl text-background-50">海量漫画，免费畅读</h2>
        <p className="text-sm md:text-base text-background-100/90 max-w-2xl leading-relaxed">
          36漫画网汇集恋爱、校园、奇幻、都市、热血、悬疑等数十个分类的海量漫画资源，支持手机与电脑端下拉式流畅阅读，每天更新不断，陪你追完每一部佳作。
        </p>
        <Link
          to="/category/all"
          className="inline-flex items-center gap-2 rounded-md bg-background-50 text-primary-700 text-sm md:text-base font-medium px-6 py-3 whitespace-nowrap hover:bg-accent-100 transition-colors cursor-pointer"
        >
          立即开始追漫 <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

function FaqSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="w-full mx-auto px-4 md:px-6 py-10 md:py-14" aria-labelledby="faq-title">
      <div className="max-w-3xl mx-auto">
        <h2 id="faq-title" className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-6">
          常见问题
        </h2>
        <div className="flex flex-col gap-3">
          {faqs.map((f, i) => (
            <div key={f.q} className="rounded-lg bg-background-100 border border-background-200/70 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left text-sm md:text-base font-medium text-foreground-900 cursor-pointer"
                aria-expanded={open === i}
              >
                {f.q}
                <i className={`ri-add-line w-5 h-5 flex items-center justify-center text-primary-600 transition-transform ${open === i ? "rotate-45" : ""}`} aria-hidden="true" />
              </button>
              {open === i && (
                <p className="px-4 pb-4 text-sm text-foreground-700 leading-relaxed">{f.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <CategorySection />
      <FeaturedSection />
      <LatestSection />
      <RankSection />
      <CtaSection />
      <FaqSection />
    </>
  );
}